import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { X, Plus, Minus, Trash2, Gift, Truck, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import api from "@/lib/api";
import { calcBundle } from "@/lib/promo";

const FREE_SHIP = 35;
const GIFT = 60;

export default function CartDrawer() {
  const { items, open, setOpen, setQty, remove, subtotal, count, add } = useCart();
  const nav = useNavigate();
  const [upsells, setUpsells] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { sort: "popular", limit: 10 } }).then((r) => setUpsells(r.data)).catch(() => {});
  }, []);

  const { freeUnits, toNextFree } = calcBundle(items);
  const toFreeShip = Math.max(0, FREE_SHIP - subtotal);
  const toGift = Math.max(0, GIFT - subtotal);
  const progress = Math.min(100, (subtotal / GIFT) * 100);

  const goCheckout = () => { setOpen(false); nav("/carrito"); };

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/50 z-[70]" onClick={() => setOpen(false)} />}
      <aside className={`fixed top-0 right-0 h-full w-full max-w-md bg-white z-[80] shadow-2xl flex flex-col transition-transform duration-300 ${open ? "translate-x-0" : "translate-x-full"}`} data-testid="cart-drawer">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="font-display font-black text-xl flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-drelo-primary" /> Tu Carrito ({count})
          </h2>
          <button onClick={() => setOpen(false)} data-testid="cart-close"><X className="w-6 h-6" /></button>
        </div>

        {/* Reward progress */}
        <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 border-b">
          <div className="text-xs font-bold mb-2">
            {toFreeShip > 0 ? (
              <span className="flex items-center gap-1.5"><Truck className="w-4 h-4 text-drelo-primary" /> Te faltan <span className="text-drelo-primary">${toFreeShip.toFixed(2)}</span> para ENVÍO GRATIS</span>
            ) : toGift > 0 ? (
              <span className="flex items-center gap-1.5"><Gift className="w-4 h-4 text-drelo-primary" /> ¡Envío gratis! Te faltan <span className="text-drelo-primary">${toGift.toFixed(2)}</span> para un REGALO 🎁</span>
            ) : (
              <span className="flex items-center gap-1.5 text-drelo-mint"><Gift className="w-4 h-4" /> ¡Genial! Has desbloqueado envío gratis + regalo 🎁</span>
            )}
          </div>
          <div className="h-2.5 bg-white rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-drelo-primary to-drelo-deal transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {items.length === 0 ? (
            <div className="text-center py-16 text-drelo-muted">
              <ShoppingBag className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-semibold">Tu carrito está vacío</p>
              <Link to="/tienda" onClick={() => setOpen(false)} className="text-drelo-primary font-bold text-sm mt-2 inline-block">Ver ofertas →</Link>
            </div>
          ) : items.map((it) => (
            <div key={it.product_id} className="flex gap-3 items-center" data-testid={`cart-item-${it.product_id}`}>
              <img src={it.image} alt={it.title} className="w-16 h-16 rounded-lg object-cover border" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold line-clamp-2 leading-tight">{it.title}</p>
                <p className="text-drelo-primary font-black">${it.price.toFixed(2)}</p>
              </div>
              <div className="flex flex-col items-end gap-1.5">
                <div className="flex items-center gap-1.5 bg-slate-100 rounded-full px-1">
                  <button onClick={() => setQty(it.product_id, it.quantity - 1)} className="p-1" data-testid={`dec-${it.product_id}`}><Minus className="w-3.5 h-3.5" /></button>
                  <span className="text-sm font-bold w-5 text-center">{it.quantity}</span>
                  <button onClick={() => setQty(it.product_id, it.quantity + 1)} className="p-1" data-testid={`inc-${it.product_id}`}><Plus className="w-3.5 h-3.5" /></button>
                </div>
                <button onClick={() => remove(it.product_id)} className="text-drelo-muted hover:text-drelo-urgency" data-testid={`remove-${it.product_id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}

          {/* 3x2 promo + upsells */}
          {items.length > 0 && (
            <>
              <div className="bg-orange-50 border border-orange-100 rounded-lg p-2.5 text-xs font-bold text-center" data-testid="drawer-bundle-hint">
                {freeUnits > 0 ? `🎁 ¡Llevas ${freeUnits} GRATIS con la promo 3x2!` : `🎁 Añade ${toNextFree} más y el más barato es GRATIS (3x2)`}
              </div>
              <div>
                <p className="text-xs font-black uppercase tracking-wide text-drelo-muted mb-2 mt-1">Completa tu pedido 👇</p>
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {upsells.filter((u) => !items.find((i) => i.product_id === u.id)).slice(0, 6).map((u) => (
                    <div key={u.id} className="w-28 shrink-0 border rounded-lg p-2" data-testid={`upsell-${u.id}`}>
                      <img src={u.image} alt={u.title} className="w-full h-16 object-cover rounded" />
                      <p className="text-[11px] font-semibold line-clamp-2 leading-tight mt-1 h-7">{u.title}</p>
                      <div className="flex items-center justify-between mt-1">
                        <span className="text-drelo-primary font-black text-xs">${u.price.toFixed(2)}</span>
                        <button onClick={() => add(u, 1)} className="bg-drelo-primary text-white w-6 h-6 grid place-items-center rounded-full" data-testid={`upsell-add-${u.id}`}><Plus className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t p-4 space-y-3">
            <div className="flex justify-between font-bold">
              <span>Subtotal</span>
              <span className="font-display font-black text-xl">${subtotal.toFixed(2)}</span>
            </div>
            <button onClick={goCheckout} className="w-full bg-drelo-primary text-white py-3.5 rounded-xl font-black uppercase tracking-wide hover:bg-drelo-hover active:scale-[0.98] transition-all" data-testid="go-checkout-btn">
              Finalizar compra
            </button>
          </div>
        )}
      </aside>
    </>
  );
}
